import { CompactSelection, type GridSelection, type Item } from "@artis/data-grid";
import { useCallback, useRef, useState } from "react";

import type { FormulaInputHandle } from "../FormulaInput";

export type Draft = { cell: Item; text: string };
export type EditorId = "bar" | "cell";

type EditSessionDeps = {
  setValue: (cell: Item, raw: string) => void;
  rowCount: number;
  colCount: number;
};

export function useEditSession({ setValue, rowCount, colCount }: EditSessionDeps) {
  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  });
  const activeCell = selection.current?.cell;
  const activeCellRef = useRef(activeCell);
  activeCellRef.current = activeCell;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  const [draft, setDraft] = useState<Draft | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const caretRef = useRef(0);
  const pickTargetRef = useRef<{ start: number; end: number } | null>(null);
  const resizeRef = useRef<{
    target: { start: number; end: number };
    anchor: { col: number; row: number };
  } | null>(null);
  const highlightTextRef = useRef<string | null>(null);
  const barElRef = useRef<FormulaInputHandle | null>(null);
  const cellElRef = useRef<FormulaInputHandle | null>(null);
  const gridWrapRef = useRef<HTMLDivElement | null>(null);
  const focusedEditorRef = useRef<EditorId | null>(null);
  const cellOwnsFocusRef = useRef(false);

  const editorFocusChanged = useCallback((editor: EditorId, focused: boolean) => {
    if (focused) {
      focusedEditorRef.current = editor;
    } else if (focusedEditorRef.current === editor) {
      focusedEditorRef.current = null;
    }
  }, []);

  const beginEdit = useCallback((cell: Item, text: string, editor: EditorId) => {
    caretRef.current = text.length;
    cellOwnsFocusRef.current = editor === "cell";
    setDraft({ cell, text });
  }, []);

  const attachCellEditor = useCallback((el: FormulaInputHandle | null) => {
    cellElRef.current = el;
    if (el === null) {
      if (focusedEditorRef.current === "cell") focusedEditorRef.current = null;
      return;
    }
    if (cellOwnsFocusRef.current) el.focus();
  }, []);

  const moveSelection = (from: Item, movement: readonly [number, number]) => {
    const col = Math.min(Math.max(from[0] + movement[0], 0), colCount - 1);
    const row = Math.min(Math.max(from[1] + movement[1], 0), rowCount - 1);
    setSelection({
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
      current: { cell: [col, row], range: { x: col, y: row, width: 1, height: 1 }, rangeStack: [] },
    });
  };
  const moveSelectionRef = useRef(moveSelection);
  moveSelectionRef.current = moveSelection;

  const commitDraft = useCallback(
    (movement?: readonly [number, number]) => {
      if (pickTargetRef.current || resizeRef.current) return;
      const current = draftRef.current;
      if (current) {
        setValue(current.cell, current.text);
        if (movement) requestAnimationFrame(() => moveSelectionRef.current(current.cell, movement));
      }
      cellOwnsFocusRef.current = false;
      draftRef.current = null;
      setDraft(null);
    },
    [setValue],
  );

  const cancelEdit = useCallback(() => {
    cellOwnsFocusRef.current = false;
    draftRef.current = null;
    setDraft(null);
    barElRef.current?.blur();
    cellElRef.current?.blur();
  }, []);

  const highlightText = draft?.text ?? null;
  highlightTextRef.current = highlightText;

  return {
    selection,
    setSelection,
    activeCell,
    activeCellRef,
    selectionRef,
    draft,
    setDraft,
    draftRef,
    caretRef,
    pickTargetRef,
    resizeRef,
    highlightTextRef,
    barElRef,
    cellElRef,
    gridWrapRef,
    focusedEditorRef,
    editorFocusChanged,
    beginEdit,
    attachCellEditor,
    moveSelectionRef,
    commitDraft,
    cancelEdit,
    highlightText,
  };
}

export type EditSession = ReturnType<typeof useEditSession>;
